import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { api } from '../../api/userAPI';

type FullUser = {
  user_id: string | number;
  username: string;
  email: string;
  is_active: boolean;
  access: string | null;
};

const LIMIT = 30;
const DEBOUNCE_MS = 300;

export const UserManagementTable: React.FC = () => {
  const [fullUsers, setFullUsers] = useState<FullUser[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [refreshIndex, setRefreshIndex] = useState(0);
  const toast = useToast();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    api.getUsersBase({ query: debouncedSearch || undefined, limit: LIMIT }).then(value => {
      setFullUsers(value.data as FullUser[]);
    });
  }, [debouncedSearch, refreshIndex]);

  const handleToggleActive = async (user: FullUser) => {
    const newIsActive = !user.is_active;
    setFullUsers(prev =>
      prev.map(u => u.email === user.email ? { ...u, is_active: newIsActive } : u)
    );
    try {
      await api.toggleUserActive(user.email, newIsActive);
      setRefreshIndex(prev => prev + 1);
      toast({ status: 'success', title: `User ${newIsActive ? 'activated' : 'deactivated'} successfully`, duration: 3000, isClosable: true });
    } catch {
      setFullUsers(prev =>
        prev.map(u => u.email === user.email ? { ...u, is_active: user.is_active } : u)
      );
      toast({ status: 'error', title: 'Failed to update user status', duration: 3000, isClosable: true });
    }
  };

  return (
    <Box mt={10} borderWidth={1} borderRadius="md" p={3} shadow="sm" overflowX="auto">
      <Heading size="sm" mb={3}>User Management</Heading>
      <Input
        placeholder="Search by email"
        mb={3}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <Box maxHeight="350px" overflowY="auto">
        <Table variant="striped" size="sm" whiteSpace={{base: "nowrap", md: "normal"}}>
          <Thead>
            <Tr>
              <Th>User ID</Th>
              <Th>Username</Th>
              <Th>Email</Th>
              <Th>Access</Th>
              <Th>Active</Th>
            </Tr>
          </Thead>
          <Tbody>
            {fullUsers.map(user => (
              <Tr key={user.email}>
                <Td>{user.user_id}</Td>
                <Td>{user.username}</Td>
                <Td>{user.email}</Td>
                <Td>{user.access ?? '-'}</Td>
                <Td>
                  <Switch
                    colorScheme="teal"
                    isChecked={user.is_active}
                    onChange={() => handleToggleActive(user)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
